
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, filter, map, of, merge, takeUntil, withLatestFrom, take } from 'rxjs';

import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { AppDataState } from '@app/shared/stores/app-data.state';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { CommonModalDialogComponent } from '@app/shared/components/common-modal-dialog/common-modal-dialog.component';
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from '@app/shared/components/common-modal-dialog/common-modal-dialog.constant';
import { DataGridColumnDef, GetReportLinkSearchByList } from './report-link.constant';
import { ReportLink } from '@app/shared/classes/report-link';
import { ReportLinkEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/report-link-edit-modal-dialog/report-link-edit-modal-dialog.component';
import { ReportLinkDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/report-link-data-grid-cell.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { DisableDeleteButtonDirective } from '@app/shared/directive/disable-delete-button.directive';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-report-link',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule, DisableDeleteButtonDirective, DisableEditButtonDirective,
    SmartSearchFormComponent, SpinnerButtonComponent, ImportDataResultComponent, ReportLinkEditModalDialogComponent, ReportLinkDataGridCell],
  templateUrl: './report-link.component.html',
  styleUrl: './report-link.component.css'
})
export class ReportLinkComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.REPORT_LINK_SEARCH_FORM;
  selectedItems: ReportLink[] = [];

  dataSource$: Observable<ReportLink[]>;
  reportLinkSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentReportLinkSearchBy) currentReportLinkSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_REPORT_LINK)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_REPORT_LINK)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_REPORT_LINK)) response$: Observable<WebApiResponse<ReportLink[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_REPORT_LINK)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_REPORT_LINK)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_REPORT_LINK;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentReportLinkSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.reportLinkSearchByList$ = of(GetReportLinkSearchByList());

    this.actions$.pipe(ofActionSuccessful(AppDataAction.DeleteReportLink), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));
  }

  rowSelected = (data: ReportLink[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetReportLink());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetReportLink(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_REPORT_LINK;
    this.store.dispatch(new AppDataAction.GetReportLink(info));
  }

  onCreate = () => {
    this.dialog.open(ReportLinkEditModalDialogComponent, {
      width: '700px',
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true
    });
  }

  onEdit = () => {
    this.dialog.open(ReportLinkEditModalDialogComponent, {
      width: '700px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result.data !== ModalDialogResult.YES) {
        return;
      }

      this.store.dispatch(new AppDataAction.DeleteReportLink(this.selectedItems.map(item => item.reportLink_Id)));
    })
  }
}
